import { DictationMic } from '@/components/shared/DictationMic'
import { useSpeechToText } from '@/hooks/useSpeechToText'
import { isProbablyMobile } from '@/lib/device'
import { useState } from 'react'

const MAX_LENGTH = 2000

interface IntakeComposerProps {
  /** Resolves true when the message was accepted; the box clears only then. */
  onSend: (text: string) => Promise<boolean>
  disabled: boolean
  sending: boolean
  placeholder: string
}

export function IntakeComposer({ onSend, disabled, sending, placeholder }: IntakeComposerProps) {
  const [draft, setDraft] = useState('')

  const { isListening, isSupported, interimTranscript, error, startListening, stopListening } =
    useSpeechToText({
      onFinalTranscript: (text) => {
        setDraft((prev) => {
          const trimmed = prev.trimEnd()
          return (trimmed ? `${trimmed} ${text}` : text).slice(0, MAX_LENGTH)
        })
      },
    })
  const canDictate = isSupported && !isProbablyMobile()

  const canSend = draft.trim().length > 0 && !disabled && !sending

  async function submit() {
    if (!canSend) return
    if (isListening) stopListening()
    const ok = await onSend(draft.trim())
    if (ok) setDraft('')
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        void submit()
      }}
      className="flex items-end gap-3"
    >
      <div className="flex-1">
        <div className="relative">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value.slice(0, MAX_LENGTH))}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault()
                void submit()
              }
            }}
            placeholder={canDictate ? `${placeholder} (or tap the mic)` : placeholder}
            rows={3}
            disabled={disabled || sending}
            aria-label="Your answer"
            className="w-full resize-none rounded-xl border border-border bg-background px-3 py-2.5 pr-10 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
          />
          <DictationMic
            isListening={isListening}
            isSupported={canDictate}
            error={error}
            disabled={disabled || sending}
            interimTranscript={interimTranscript}
            onToggle={isListening ? stopListening : startListening}
          />
        </div>
      </div>
      <button
        type="submit"
        disabled={!canSend}
        className="rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-40"
      >
        {sending ? '…' : 'Send'}
      </button>
    </form>
  )
}
